/**
 * An order's status, and the checklist that moves it.
 *
 * A work order and a material order each have a three-rung checklist, and the
 * status is meant to say which rung the order has reached. Ticking a rung set
 * the status; un-ticking it did not put the status back. "Undo" on Paid cleared
 * `paid_at` and left `status = 'paid'` behind, so the order still counted as
 * paid to everything that asks — including the guard that refuses to delete an
 * estimate with a paid order on it (`canDeleteEstimate`) and the database
 * trigger behind it. Unwinding a payment was impossible from the checklist, and
 * the button said otherwise.
 *
 * So the status is derived from the stamps rather than assigned by whichever
 * one was clicked last: it is the highest rung that carries a date, or the
 * order's starting status when none do. Un-ticking Order Placed on an order
 * that has been paid keeps it paid, which is the same rule read the other way.
 */

import type { MaterialOrder, WorkOrder } from "./types.js";

/**
 * One rung: the date column the tick writes, and the status it means.
 *
 * Parameterised by the row it reads so a step cannot name a column the order
 * does not have — a typo here would silently leave a rung permanently un-ticked.
 */
export interface OrderChecklistStep<T> {
  column: keyof T & string;
  status: string;
}

/**
 * Just the date columns, and all of them optional: a caller passes the whole
 * order row, and a test passes the two stamps the case is about.
 */
export type WorkOrderChecklist = Partial<
  Pick<WorkOrder, "scheduled_at" | "completed_at" | "paid_at">
>;
export type MaterialOrderChecklist = Partial<
  Pick<MaterialOrder, "ordered_at" | "picked_up_at" | "paid_at">
>;

/** Lowest rung first — the order in which the work actually happens. */
export const WORK_ORDER_STEPS: readonly OrderChecklistStep<WorkOrderChecklist>[] =
  [
    { column: "scheduled_at", status: "scheduled" },
    { column: "completed_at", status: "completed" },
    { column: "paid_at", status: "paid" },
  ];

export const MATERIAL_ORDER_STEPS: readonly OrderChecklistStep<MaterialOrderChecklist>[] =
  [
    { column: "ordered_at", status: "ordered" },
    { column: "picked_up_at", status: "picked_up" },
    { column: "paid_at", status: "paid" },
  ];

export const WORK_ORDER_BASE_STATUS = "not_scheduled";
export const MATERIAL_ORDER_BASE_STATUS = "not_placed";

/**
 * The status a set of checklist dates adds up to.
 *
 * The highest rung with a date wins, so a gap in the middle — paid without ever
 * being marked complete — still reads as paid rather than as unstarted.
 */
export function statusForChecklist<T>(
  steps: readonly OrderChecklistStep<T>[],
  stamps: T,
  base: string,
): string {
  let status = base;
  for (const step of steps) {
    if (stamps[step.column]) status = step.status;
  }
  return status;
}

/**
 * The patch for ticking or un-ticking one rung.
 *
 * Returns both the date column and the status so the two can never drift: a
 * caller that writes one writes the other in the same update.
 */
export function toggleChecklistStep<T>(
  steps: readonly OrderChecklistStep<T>[],
  order: T,
  column: keyof T & string,
  base: string,
  now: string,
): Record<string, string | null> {
  const stamp = order[column] ? null : now;

  let status = base;
  for (const step of steps) {
    const value = step.column === column ? stamp : order[step.column];
    if (value) status = step.status;
  }

  return { [column]: stamp, status };
}

/**
 * Why a material order cannot be placed yet, or null when it can.
 *
 * Jonathon, 2026-09-30: "we shouldn't be able to place or mark a material
 * order placed without a supplier selected". An order with no supplier has
 * nobody to go to, and every later step — the bill, the receipt check, the
 * QuickBooks vendor — reads the supplier off it. The database refuses it too
 * (migration 20260930300000); this is the sentence the screen shows first.
 * ADR 0243.
 */
export const MATERIAL_ORDER_NEEDS_SUPPLIER =
  "Choose a supplier before placing this order.";

export function placingBlockedBy(order: {
  supplier_id?: string | null;
}): string | null {
  return order.supplier_id ? null : MATERIAL_ORDER_NEEDS_SUPPLIER;
}

/**
 * What placing a material order writes — the first time, or again.
 *
 * "Send again" reopens the screen that places the order, and that screen used
 * to write `ordered_at = now` and `status = 'ordered'` every time. Resending an
 * order that had been delivered and paid put it back to Ordered with both of
 * those still ticked, and replaced the day it was really placed. So an order
 * that is already placed keeps its ticks, and the resend writes only what is
 * new: that it was emailed, and to whom.
 *
 * Placing it the first time ticks the rung through `toggleChecklistStep`, so
 * the status is what all the ticks add up to rather than always "ordered" — an
 * order paid for before it was marked placed stays paid.
 *
 * The email stamp only when it actually went by email: an order given over a
 * counter is placed, and claiming an email for it would be the same lie in the
 * other direction. The credit stamp only when the letter actually asked:
 * marking an order placed off the back of a phone call must not spend the
 * job's one claim.
 */
export function placeMaterialOrderPatch(
  order: MaterialOrderChecklist,
  sent: {
    now: string;
    emailedTo?: string | null | undefined;
    creditAsked?: boolean;
  },
): Record<string, string | null> {
  const patch: Record<string, string | null> = order.ordered_at
    ? {}
    : toggleChecklistStep(
        MATERIAL_ORDER_STEPS,
        order,
        "ordered_at",
        MATERIAL_ORDER_BASE_STATUS,
        sent.now,
      );
  if (sent.emailedTo) {
    patch.supplier_emailed_at = sent.now;
    patch.supplier_emailed_to = sent.emailedTo;
  }
  if (sent.creditAsked) {
    patch.quickmeasure_credit_requested_at = sent.now;
  }
  return patch;
}

/**
 * The status, as a person reads it.
 *
 * The stored values are `not_placed`, `picked_up` and so on, and they were on
 * screen exactly like that — in a dropdown, which also let somebody set a
 * status the checklist disagreed with. Jonathon, 2026-09-12, chose the
 * checklist: the status is what the ticks add up to, and the screen says it in
 * words rather than offering it as a choice.
 *
 * Anything unrecognised is spelled out rather than hidden, because a status
 * this does not know about is a status somebody needs to see.
 */
export function orderStatusLabel(status: string | null | undefined): string {
  const known: Record<string, string> = {
    not_placed: "Not placed",
    ordered: "Ordered",
    picked_up: "Picked up",
    not_scheduled: "Not scheduled",
    scheduled: "Scheduled",
    completed: "Completed",
    paid: "Paid",
  };
  const value = (status ?? "").trim();
  if (value === "") return "—";
  return (
    known[value] ??
    value.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())
  );
}

/**
 * Why this work order cannot be marked complete yet, or null when it can.
 *
 * Jonathon, 2026-09-22: a work order must not be ticked complete with no crew
 * on it. "Complete" is not only a label — it is what the payout board reads to
 * decide who is owed for the work (ADR 0180), what the balance letter and the
 * QuickBooks invoice wait on, and what the job's "All Work Complete" step is
 * built from. An order complete with `crew_id` null says work was finished and
 * names nobody who finished it, so the payout has no one to pay and the money
 * moves on a job whose labour cost can never be settled.
 *
 * Only on the way in. Un-ticking an order that is already complete is always
 * allowed, including one that reached "complete" without a crew before this
 * rule existed — otherwise the fix for the bad row would be blocked by the
 * guard against it.
 *
 * Here rather than in the editor so the MCP tools and any later screen refuse
 * it the same way, with the same sentence.
 */
export function workCompleteRefusal(
  order: Pick<WorkOrder, "crew_id" | "completed_at">,
): string | null {
  if (order.completed_at) return null;
  if ((order.crew_id ?? "").trim() !== "") return null;
  return "Choose the crew that did the work before marking it complete.";
}
